import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  useTheme,
  Chip,
  Stack,
  Pagination,
  CircularProgress,
  Alert,
  Tabs,
  Tab
} from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import UndoIcon from '@mui/icons-material/Undo';
import InfoIcon from '@mui/icons-material/Info';
import logger from '../utils/logger';
import AllInclusiveIcon from '@mui/icons-material/AllInclusive';
import { getPointsTransactions } from '../store/actions/orderAction';

const PointsRecord = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { pointsTransactions } = useSelector((state) => state.order);
  const { user } = useSelector((state) => state.auth);

  // 交易记录筛选状态
  const [transactionFilter, setTransactionFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // 获取交易记录
  const fetchPointsTransactions = async (page = 1, type = 'all') => {
    try {
      const params = { page, limit: itemsPerPage };
      if (type !== 'all') params.type = type;
      await dispatch(getPointsTransactions(params));
    } catch (error) {
      logger.error('获取积分交易记录失败:', error);
    }
  };

  // 初始加载交易记录
  useEffect(() => {
    if (user) {
      fetchPointsTransactions(1, transactionFilter);
    }
  }, [user, transactionFilter, dispatch]);

  // 处理页码变化
  const handlePageChange = (event, value) => {
    setCurrentPage(value);
    fetchPointsTransactions(value, transactionFilter);
  };

  // 处理交易类型筛选变化
  const handleTransactionFilterChange = (event, newValue) => {
    setTransactionFilter(newValue);
    setCurrentPage(1);
    fetchPointsTransactions(1, newValue);
  };

  // 获取交易类型的显示文本和颜色
  const getTransactionTypeInfo = (type) => {
    switch (type) {
      case 'recharge':
        return {
          text: '充值',
          color: theme.palette.success.main,
          bgColor: 'rgba(76, 175, 80, 0.1)',
          icon: <AddIcon sx={{ fontSize: 16 }} />
        };
      case 'consume':
        return {
          text: '消费',
          color: theme.palette.error.main,
          bgColor: 'rgba(244, 67, 54, 0.1)',
          icon: <RemoveIcon sx={{ fontSize: 16 }} />
        };
      case 'refund':
        return {
          text: '退款',
          color: theme.palette.warning.main,
          bgColor: 'rgba(255, 193, 7, 0.1)',
          icon: <UndoIcon sx={{ fontSize: 16 }} />
        };
      case 'invite_reward':
        return {
          text: '邀请奖励',
          color: theme.palette.info.main,
          bgColor: 'rgba(33, 150, 243, 0.1)',
          icon: <AddIcon sx={{ fontSize: 16 }} />
        };
      default:
        return {
          text: type,
          color: theme.palette.text.primary,
          bgColor: 'rgba(0, 0, 0, 0.05)',
          icon: <InfoIcon sx={{ fontSize: 16 }} />
        };
    }
  };

  // 格式化日期
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // 解码文件名
  const decodeFileName = (fileName) => {
    try {
      return decodeURIComponent(fileName);
    } catch (e) {
      return fileName;
    }
  };

  // 解码交易描述中的文件名
  const decodeTransactionDescription = (description) => {
    try {
      const fileNameMatch = description.match(/^(.*?:\s*)(.+)$/);
      if (fileNameMatch) {
        const prefix = fileNameMatch[1];
        const fileName = fileNameMatch[2];
        if (fileName.includes('%')) {
          return prefix + decodeURIComponent(fileName);
        }
      }
      return description;
    } catch (e) {
      return description;
    }
  };

  return (
    <Box sx={{ height: '100%' }}>
      {/* 页面标题 */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontWeight: 600,
            background: 'linear-gradient(90deg, #4353FF 0%, #4353FF 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            marginLeft: '10px'
          }}
        >
          点数记录
        </Typography>
      </Box>

      {/* 点数交易记录卡片 */}
      <Card sx={{ p: 3, borderRadius: 3 }}>
        {/* 交易类型筛选标签 */}
        <Box sx={{ mb: 3 }}>
          <Tabs
            value={transactionFilter}
            onChange={handleTransactionFilterChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTabs-indicator': {
                height: 3,
                borderRadius: '3px 3px 0 0'
              }
            }}
          >
            <Tab
              value="all"
              icon={<AllInclusiveIcon />}
              iconPosition="start"
              label="全部"
              sx={{ textTransform: 'none', minHeight: 48 }}
            />
            <Tab
              value="recharge"
              icon={<AddIcon />}
              iconPosition="start"
              label="充值"
              sx={{ textTransform: 'none', minHeight: 48, color: theme.palette.success.main }}
            />
            <Tab
              value="consume"
              icon={<RemoveIcon />}
              iconPosition="start"
              label="消费"
              sx={{ textTransform: 'none', minHeight: 48, color: theme.palette.error.main }}
            />
            <Tab
              value="refund"
              icon={<UndoIcon />}
              iconPosition="start"
              label="退款"
              sx={{ textTransform: 'none', minHeight: 48, color: theme.palette.warning.main }}
            />
          </Tabs>
        </Box>

        {/* 错误状态显示 */}
        {pointsTransactions.error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {pointsTransactions.error}
          </Alert>
        )}

        {pointsTransactions.loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : pointsTransactions.data.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <AccountBalanceWalletIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              暂无交易记录
            </Typography>
            <Typography variant="body2" color="text.disabled">
              {transactionFilter === 'all' ? '还没有任何交易记录' : `还没有${getTransactionTypeInfo(transactionFilter).text}记录`}
            </Typography>
          </Box>
        ) : (
          <>
            <Stack spacing={2} sx={{ width: '100%' }}>
              {pointsTransactions.data.map((transaction) => {
                const typeInfo = getTransactionTypeInfo(transaction.type);
                const isPositive = transaction.points > 0;

                return (
                  <Card
                    key={transaction._id}
                    sx={{
                      p: 3,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 25px rgba(0,0,0,0.12)'
                      },
                      borderRadius: 3,
                      border: '1px solid',
                      borderColor: 'divider'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                      {/* 左侧图标 */}
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: typeInfo.bgColor,
                          color: typeInfo.color,
                          flexShrink: 0
                        }}
                      >
                        {typeInfo.icon}
                      </Box>

                      {/* 主要内容 */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        {/* 第一行：交易类型和点数变化 */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip
                              label={typeInfo.text}
                              size="small"
                              sx={{
                                bgcolor: typeInfo.bgColor,
                                color: typeInfo.color,
                                fontWeight: 600,
                                '& .MuiChip-label': { px: 1.5 }
                              }}
                            />
                          </Box>

                          {/* 点数变化 */}
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 700,
                              color: isPositive ? theme.palette.success.main : theme.palette.error.main
                            }}
                          >
                            {isPositive ? '+' : ''}{transaction.points}
                          </Typography>
                        </Box>

                        {/* 第二行：描述信息 */}
                        <Typography variant="body2" color="text.primary" sx={{ mb: 2, lineHeight: 1.5 }}>
                          {decodeTransactionDescription(transaction.description)}
                        </Typography>

                        {/* 第三行：详细信息标签 */}
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                          {/* 任务信息 */}
                          {transaction.task_id && (
                            <>
                              <Chip
                                label={`任务: ${transaction.task_id.task_id}`}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '0.7rem' }}
                              />
                              <Chip
                                label={decodeFileName(transaction.task_id.file_name)}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '0.7rem', maxWidth: 200 }}
                              />
                            </>
                          )}

                          {/* 充值信息 */}
                          {transaction.payment_info && (
                            <>
                              <Chip
                                label={`订单: ${transaction.payment_info.order_id}`}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '0.7rem' }}
                              />
                              <Chip
                                label={transaction.payment_info.payment_method_text}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '0.7rem' }}
                              />
                              <Chip
                                label={`¥${transaction.payment_info.payment_amount}`}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '0.7rem' }}
                              />
                              {transaction.payment_info.sku_info && (
                                <Chip
                                  label={transaction.payment_info.sku_info.sku_name}
                                  size="small"
                                  variant="outlined"
                                  sx={{ fontSize: '0.7rem' }}
                                />
                              )}
                            </>
                          )}

                          {/* 余额信息 */}
                          <Chip
                            label={`余额: ${transaction.balance}`}
                            size="small"
                            variant="filled"
                            sx={{
                              fontSize: '0.7rem',
                              bgcolor: 'rgba(0,0,0,0.06)',
                              color: 'text.secondary'
                            }}
                          />
                        </Box>

                        {/* 第四行：时间信息 */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(transaction.created_at)}
                          </Typography>

                          {/* 支付时间（充值记录） */}
                          {transaction.payment_info?.paid_at && (
                            <Typography variant="caption" color="text.secondary">
                              支付时间: {formatDate(transaction.payment_info.paid_at)}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </Card>
                );
              })}
            </Stack>

            {/* 分页组件 */}
            {pointsTransactions.pagination.total_pages > 1 && (
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mt: 4,
                mb: 1
              }}>
                <Typography variant="body2" color="text.secondary">
                  显示第 {((pointsTransactions.pagination.current_page - 1) * pointsTransactions.pagination.page_size) + 1}-{Math.min(pointsTransactions.pagination.current_page * pointsTransactions.pagination.page_size, pointsTransactions.pagination.total_records)} 项，
                  共 {pointsTransactions.pagination.total_records} 项
                </Typography>

                <Pagination
                  count={pointsTransactions.pagination.total_pages}
                  page={pointsTransactions.pagination.current_page}
                  onChange={handlePageChange}
                  color="primary"
                  shape="rounded"
                  showFirstButton
                  showLastButton
                  disabled={pointsTransactions.loading}
                />
              </Box>
            )}
          </>
        )}
      </Card>
    </Box>
  );
};

export default PointsRecord;